/**
 * The status banner, drawn in characters.
 *
 * **Whole columns are the whole point.** The banner spans the terminal, but it
 * spans it in characters: the caller measures how many fit and this returns
 * lines of exactly that many code points, so the box always closes on a
 * character boundary. That is what makes it read as a terminal status line
 * rather than a web header styled to look like one — a box drawn with CSS would
 * end mid-glyph and the illusion would be gone.
 *
 * It held 80 columns until 2026-08-10, on the argument that a responsive banner
 * would reflow on every resize. It reflows; the illusion survives it, and a box
 * stopping two thirds of the way across the window was the more visible tell.
 * `use-banner-cols.ts` is where the measuring happens.
 *
 * It is a pure function of numbers already on screen, which is what makes it
 * testable without a DOM, the way `commands.ts` is. It formats; it does not
 * compute. Every value it prints was handed to it.
 *
 * **On width:** the unit is the code point, not the UTF-16 unit and not the
 * rendered pixel. `[...line].length` is exactly `cols` for every line returned,
 * which is the property `banner.test.ts` asserts. That equals visual alignment
 * only in a font whose glyphs all share one advance width — true of the
 * monospace stack in `styles.css` for the box-drawing characters and the
 * ▲/▼/· used here, and the reason no other non-ASCII glyph is used. Each of
 * those was measured in a browser at the same advance as an ASCII character;
 * adding a fourth means measuring it, not assuming it.
 */

export type BannerQuote = {
  symbol: string;
  /** Already formatted for reading: the banner does no arithmetic. */
  last: string;
  direction: "up" | "down" | "";
  /** Already formatted: `+1.51%`, or "" when there is no reference. */
  change: string;
  /**
   * Whether the venue was open when the price was taken.
   *
   * The banner is drawn entirely in `--fg-dim`, so it has no colour channel to
   * spend on this: a shut market is said with the arrow's ABSENCE, and with the
   * `·` that stands in its place so the columns do not shift when a venue opens.
   */
  open: boolean;
};

export type BannerModel = {
  /** `equity 100,412.55` pairs, in the order they should read. */
  stats: readonly { label: string; value: string }[];
  /** Right-aligned on the first row: the connection, or who is signed in. */
  right: string;
  quotes: readonly BannerQuote[];
  /** Shown in place of the ticker when there is nothing to show. */
  quiet: string;
  /**
   * Right-aligned on the ticker row: what the venue is doing, in its own clock.
   *
   * It belongs beside the prices rather than beside the money, because it is
   * the sentence that explains them — a row of numbers that has not moved all
   * evening is a mystery until something on screen says the market is shut.
   * The ticker row's right-hand field was empty until M4/I6.
   */
  market: string;
};

/**
 * The column count to draw at when there is nothing to measure — a server
 * render, a test, the first paint before layout. 80 is the width a terminal has
 * always been, which makes it the right guess to be wrong with.
 */
export const BANNER_COLS = 80;

/** Narrower than this and the box is more border than content. */
const MIN_COLS = 12;

/**
 * How many rows the ticker may grow to before it starts saying "and N more".
 *
 * The banner is a status line, and a status line that fills a phone screen has
 * stopped being one — the scrollback underneath is what the user is actually
 * reading. But the cap has to clear the case that actually happens: on a phone
 * one symbol fills a row, and the terminal watches five from its first paint,
 * so anything under five reintroduces the very bug this replaced. Eight leaves
 * headroom for a hand-picked list and still bounds the height.
 *
 * Past the cap the overflow is COUNTED rather than dropped, so a symbol never
 * disappears silently the way it did when the row was clipped.
 */
const MAX_TICKER_ROWS = 8;

const points = (s: string) => [...s];
const width = (s: string) => points(s).length;

/** Truncate with an ellipsis, never wrap — a wrapped banner is a banner of unknown height. */
function clip(s: string, max: number): string {
  if (max <= 0) return "";
  const cp = points(s);
  if (cp.length <= max) return s;
  if (max === 1) return "…";
  return `${cp.slice(0, max - 1).join("")}…`;
}

/**
 * One boxed row: `│ left …gap… right │`, exactly `cols` code points.
 *
 * The right-hand text is dropped entirely rather than truncated, because half a
 * connection status is worse than none. The left is clipped to whatever remains.
 */
function row(left: string, right: string, cols: number): string {
  const inner = cols - 4; // two borders, two spaces of padding
  let r = right;
  // The right-hand field, with its gap, may never take more than half the row.
  // Past that it is crowding out the numbers, which are what the banner is for.
  if (width(r) + 2 > inner / 2) r = "";
  const room = inner - (r ? width(r) + 2 : 0);
  const l = clip(left, room);
  const gap = inner - width(l) - width(r);
  return `│ ${l}${" ".repeat(Math.max(gap, 0))}${r} │`;
}

/**
 * The one glyph after a price: which way the symbol has gone today.
 *
 * **It survives the closing bell**, and that changed in M4/I9. While the arrow
 * meant the last tick-to-tick move, a shut venue genuinely had no direction to
 * show and the glyph became a `·`. The arrow means the DAY now, and what a
 * stock did today is still true at 16:01 — it is the last word on the session,
 * and a row that forgets it the moment the market closes is less useful than
 * one that keeps it. Whether the venue is trading is said in words, on this
 * same row, by `model.market`.
 *
 * A space rather than nothing when there is no direction at all: the glyph
 * occupies a column, and a row that gives the column back would reflow the
 * whole ticker the moment one symbol lost its reference.
 */
function mark(q: BannerQuote): string {
  return q.direction === "up" ? "▲" : q.direction === "down" ? "▼" : " ";
}

/**
 * Pack words into as few rows as they fit in.
 *
 * The first row is narrower than the rest, because something is right-aligned
 * on it — the connection status above the numbers, the venue's state beside the
 * prices. Nothing is broken across a boundary: a word that will not fit on the
 * current row starts the next one, and only a word too wide for a row of its
 * own is ever clipped.
 *
 * Written for the stats row in M4/I8 and given to the ticker in M4/I10, because
 * a clipped ticker is the worse loss of the two: a stat that does not fit is
 * one number missing, and a symbol past the right edge is a price the user
 * asked to watch and cannot see, with no scrollbar to reach it — the banner is
 * text, not an element that scrolls.
 */
function pack(words: readonly string[], firstRoom: number, room: number): string[] {
  if (words.length === 0) return [""];

  const rows: string[] = [];
  let current = "";

  for (const word of words) {
    const limit = rows.length === 0 ? firstRoom : room;
    const candidate = current === "" ? word : `${current}   ${word}`;
    if (current !== "" && width(candidate) > limit) {
      rows.push(current);
      current = word;
      continue;
    }
    current = candidate;
  }
  rows.push(current);
  return rows;
}

/**
 * Keep the ticker to a status line's worth of rows, and say what was left out.
 *
 * Counting the remainder is the whole point. The row used to be clipped at the
 * right edge, which meant a symbol the user had explicitly asked to watch was
 * simply not there, with nothing to indicate it existed — the failure this
 * function exists to make impossible. `+3 more` is a small loss that announces
 * itself; a missing NVDA is a large one that does not.
 */
function capRows(rows: string[], total: number, room: number): string[] {
  if (rows.length <= MAX_TICKER_ROWS) return rows;

  const kept = rows.slice(0, MAX_TICKER_ROWS);
  // Words are `SYMBOL price ▲ +1.2%` joined by three spaces, so counting the
  // separators back out is how many symbols made it onto the kept rows.
  const shown = kept.reduce((n, line) => n + line.split("   ").length, 0);
  const note = `+${Math.max(total - shown, 1)} more`;

  const last = kept[MAX_TICKER_ROWS - 1] as string;
  kept[MAX_TICKER_ROWS - 1] =
    width(last) + 3 + width(note) <= room ? `${last}   ${note}` : clip(note, room);
  return kept;
}

/**
 * A top rule, the numbers, the ticker, a bottom rule.
 *
 * **The shape does not change as VALUES change** — that is the property the
 * scrollback beneath depends on, and it still holds: a number growing a digit
 * never moves a line. What the height depends on is the width it is drawn at
 * and how many stats and quotes there are — all of which already changed the
 * banner's content. It held four lines at every width until M4/I8, at the cost
 * of clipping equity to an ellipsis on a phone, and the ticker was still one
 * clipped row until M4/I10. A status line that drops the numbers it exists to
 * show is worse than one that is a few rows taller in a narrow window; the
 * height is bounded instead, by `MAX_TICKER_ROWS`.
 */
export function renderBanner(cols: number, model: BannerModel): string[] {
  const c = Math.max(cols, MIN_COLS);
  const rule = "─".repeat(c - 2);
  const inner = c - 4;

  // What the connection status leaves for the numbers beside it. `row` drops it
  // when it would take more than half, and this has to agree with that or the
  // packing reserves room nothing uses.
  const right = width(model.right) + 2 > inner / 2 ? "" : model.right;
  const firstRoom = inner - (right ? width(right) + 2 : 0);

  // An unlabelled stat is a sentence, not a figure — "not signed in" reads
  // wrong indented by the space a missing label would leave behind.
  const stats = pack(
    model.stats.map((s) => (s.label ? `${s.label} ${s.value}` : s.value)),
    firstRoom,
    inner,
  );

  // The venue's state sits on the FIRST ticker row, so it stays beside the
  // prices it explains rather than drifting to the bottom of a tall banner.
  const tickerRight = width(model.market) + 2 > inner / 2 ? "" : model.market;
  const ticker = capRows(
    pack(
      model.quotes.length === 0
        ? [model.quiet]
        : model.quotes.map(
            (q) => `${q.symbol} ${q.last} ${mark(q)}${q.change ? ` ${q.change}` : ""}`,
          ),
      inner - (tickerRight ? width(tickerRight) + 2 : 0),
      inner,
    ),
    model.quotes.length,
    inner,
  );

  return [
    `┌${rule}┐`,
    ...stats.map((line, i) => row(line, i === 0 ? model.right : "", c)),
    ...ticker.map((line, i) => row(line, i === 0 ? model.market : "", c)),
    `└${rule}┘`,
  ];
}
